import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { BadgeTone } from '@/shared/ui/StatusBadge';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Link } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useApproveClassification, useReviewQueue } from '../api/reviewQueue';
import type { ReviewRow, ReviewTone } from '../model/review';

/* Tone -> colour, in one place.
 *
 * `unknown` is deliberately NOT the same colour as `authoritative`, and not the same as a "PII
 * found" warning either. It is its own signal, because "the classifier failed and we do not know" is
 * a third thing: it needs a re-run, not a policy decision. Rendering it in the approved colour is
 * the single worst bug this screen could have.
 *
 * `--status-*` tokens come from `shared/ui/tokens.css`, so a theme change moves them together. */
/* Bridged to the kit's `StatusBadge` rather than drawing a chip here.
 *
 * Six screens each carried an identical outlined `<span>` — a 1px border used as the border *and*
 * the text colour — which is the chip `StatusBadge` was retreated away from. So one state rendered
 * two different ways depending on which screen a reader was on, and the outline carried the whole
 * signal in a line thin enough to be the first thing lost to a low-contrast display. The badge gives
 * three signals: a tinted ground, a dot, and the label's words.
 *
 * The domain tone stays domain-shaped and a total map bridges it, rather than these screens adopting
 * the kit's vocabulary — the domain name records *why* a state got the tone it has, and collapsing
 * the two would lose that. `satisfies Record<…>` makes a new domain state a compile error rather
 * than a badge that renders with the fallback tone. */
const BADGE_TONE = {
  authoritative: 'ok',
  'awaiting-human': 'info',
  unknown: 'unknown',
  superseded: 'warn',
} satisfies Record<ReviewTone, BadgeTone>;

function StateBadge({ row }: { readonly row: ReviewRow }): ReactElement {
  const { t } = useTranslation();
  const tone = BADGE_TONE[row.tone];

  return (
    <StatusBadge
      tone={tone}
      label={t(`review.state.${row.state}.label`)}
      /* The state's meaning as a title, not just its name. `inconclusive` alone tells a reviewer
       * nothing; "PII status unknown — the classifier did not complete" tells them what to do. */
      title={t(`review.state.${row.state}.meaning`)}
    />
  );
}

function PiiSummary({ row }: { readonly row: ReviewRow }): ReactElement {
  const { t } = useTranslation();

  /* Findings first, whatever the state. A `proposed` record's findings are the whole reason the
   * reviewer is here — an earlier version checked the state before the findings and rendered
   * "Not assessed" over a real verdict, hiding what was being reviewed. */
  if (row.findings.length === 0) {
    /* "No PII found" is a positive claim and only a human may make it. Every other empty record
     * means nobody looked or the look failed, which is not the same thing and must not read as
     * though it were. */
    return (
      <span className="lq-muted">
        {row.claimsNoPii ? t('review.pii.none') : t('review.pii.notAssessed')}
      </span>
    );
  }

  return (
    <span>
      {t('review.pii.columns', { count: row.findings.length })}
      <span className="lq-muted">
        {' — '}
        {/* Column *and* kind. A column named `notes` judged to hold an Aadhaar number is exactly
            the case where the name on its own tells a reviewer nothing. */}
        {row.findings
          .map((finding) => `${finding.column} (${t(finding.labelKey)})`)
          .join(', ')}
      </span>
    </span>
  );
}

function DatasetCell({
  datasetId,
  names,
}: {
  readonly datasetId: string;
  readonly names: ReadonlyMap<string, string> | undefined;
}): ReactElement {
  /* A name, and a link to the dataset. Approving a PII verdict is precisely the decision that needs
   * it: "secret_ref holds personal data" means something different on `catalog.connection` than on
   * an audit table, and a reviewer shown only `3b186034-…` has to go and look it up before they can
   * decide anything.
   *
   * Falls back to the id rather than to a placeholder — a dataset harvested since this page loaded
   * still gets a working link. */
  const name = names?.get(datasetId);
  return (
    <Link to={`/datasets/${datasetId}`}>
      {name ?? <span className="lq-mono">{datasetId}</span>}
    </Link>
  );
}

export function ClassificationReview(): ReactElement {
  const { t } = useTranslation();
  const queue = useReviewQueue();
  const approval = useApproveClassification();
  /* One query for the page, sharing the cache the catalog browser already fills. A `useDataset` per
   * row would be the N+1 the list endpoint exists to avoid, and a failure here degrades to ids
   * rather than blanking the queue. */
  const names = useDatasetNames();

  if (queue.isPending) {
    return (
      <>
        <PageHeader title={t('review.title')} />
        <p role="status" className="lq-muted">
          {t('review.loading')}
        </p>
        <SkeletonRows rows={5} columns={5} />
      </>
    );
  }

  if (queue.isError) {
    return (
      <>
        <PageHeader title={t('review.title')} />
        <Alert tone="error">
          {t('review.unreachable', { detail: queue.error.message })}
        </Alert>
      </>
    );
  }

  const rows = queue.data;

  return (
    <>
      <PageHeader
        title={t('review.title')}
        lede={t('review.subtitle', { count: rows.length })}
      />

      {approval.isError && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          {/* The server's own words. "cannot approve a verdict computed against a different schema
              state" is actionable; a generic failure message is not — and this is exactly the 409 a
              reviewer hits when the schema moved while their screen was open. */}
          <Alert tone="error">{approval.error.message}</Alert>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState title={t('review.emptyTitle')} guidance={t('review.empty')} />
      ) : (
        <Card flush>
          <DataTable
            caption={t('review.tableCaption')}
            columns={[
              { key: 'dataset', label: t('review.column.dataset') },
              { key: 'state', label: t('review.column.state') },
              { key: 'pii', label: t('review.column.pii') },
              { key: 'why', label: t('review.column.why') },
              { key: 'actions', label: t('review.column.actions'), actions: true },
            ]}
          >
            {rows.map((row) => (
              <tr key={row.datasetId}>
                {/* `.lumiq-numeric` is the documented mechanism for monospaced, tabular figures —
                    a component reading --lumiq-font-mono directly would skip the semantic tier and
                    survive a per-tenant brand override unchanged. */}
                <td>
                  <DatasetCell datasetId={row.datasetId} names={names.data?.byId} />
                </td>
                <td>
                  <StateBadge row={row} />
                </td>
                <td>
                  <PiiSummary row={row} />
                </td>
                <td className="lq-muted">
                  {row.detail ?? '—'}
                  {row.approvedBy !== null && (
                    /* Kept visible on a `stale` row: who approved the superseded verdict is the
                     * useful fact for whoever reviews it next. */
                    <div className="lq-cell__secondary">
                      {t('review.previouslyApprovedBy', { who: row.approvedBy })}
                    </div>
                  )}
                </td>
                <td className="lq-table__actions">
                  <Button
                    /* Disabled for `inconclusive`, because there is no verdict to approve and the
                     * server refuses it with a 409. Not offering the action is a better failure than
                     * offering it and rejecting it.
                     *
                     * `Button` rather than a bare `<button>`: the hand-rolled one carried
                     * `cursor: not-allowed` and `opacity` alongside `disabled`, which is exactly the
                     * pair that drifts apart into a control that looks unavailable and still fires. */
                    disabled={!row.canApprove}
                    busy={approval.isPending}
                    onClick={() => {
                      approval.mutate(row.datasetId);
                    }}
                    title={row.canApprove ? undefined : t('review.cannotApprove')}
                  >
                    {t('review.approve')}
                  </Button>
                </td>
              </tr>
            ))}
          </DataTable>
        </Card>
      )}
    </>
  );
}
